import { ReactElement } from 'react';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { MIME_TYPES } from '../../enums/MimeEnum';
import { ScorecardImage } from '../../types/Course';
import { PASSWORD_MANAGER_IGNORE_INPUT_PROPS } from '../../utils/formInputProps';
import GratiFileUpload from '../common/GratiFileUpload';

interface CourseScorecardEditorProps {
  courseId: string;
  scorecardImages: ScorecardImage[];
  onScorecardImagesChanged: (images: ScorecardImage[]) => void;
}

export default function CourseScorecardEditor(props: CourseScorecardEditorProps): ReactElement {
  const { courseId, scorecardImages, onScorecardImagesChanged } = props;
  const storageLocation = `course/${courseId}/publicFile`;

  const handleFileAdded = (fileName: string, contentType?: string) => {
    onScorecardImagesChanged([...scorecardImages, { fileName, contentType }]);
  };

  const handleFileDeleted = (fileName: string) => {
    onScorecardImagesChanged(scorecardImages.filter((img) => img.fileName !== fileName));
  };

  const handleMetadataChange = (fileName: string, field: 'source' | 'date', value: string) => {
    onScorecardImagesChanged(
      scorecardImages.map((img) =>
        img.fileName === fileName ? { ...img, [field]: value || undefined } : img
      )
    );
  };

  const renderExtraRowContent = (fileName: string): ReactElement | null => {
    const entry = scorecardImages.find((img) => img.fileName === fileName);
    return (
      <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
        <TextField
          label="Source"
          aria-label="Scorecard source"
          slotProps={{ htmlInput: PASSWORD_MANAGER_IGNORE_INPUT_PROPS }}
          size="small"
          value={entry?.source ?? ''}
          onChange={({ target }) => handleMetadataChange(fileName, 'source', target.value)}
          sx={{ flex: 1 }}
        />
        <TextField
          label="Date"
          aria-label="Scorecard date"
          size="small"
          type="date"
          value={entry?.date ?? ''}
          onChange={({ target }) => handleMetadataChange(fileName, 'date', target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: '10rem', flexShrink: 0 }}
        />
      </Stack>
    );
  };

  return (
    <GratiFileUpload
      storageLocation={storageLocation}
      storageFilePrefix={`${courseId}-scorecard`}
      fileNames={scorecardImages.map((img) => img.fileName)}
      maxNumFiles={20}
      label="Scorecard Images"
      thumbnailSuffix="-thumbnail"
      allowedTypes={[MIME_TYPES.JPEG, MIME_TYPES.PNG, MIME_TYPES.WEBP, MIME_TYPES.PDF]}
      onFileAdded={handleFileAdded}
      onFileDeleted={handleFileDeleted}
      renderExtraRowContent={renderExtraRowContent}
    />
  );
}
